<template lang="pug">
.page.page-muse
  //- Hero
  .muse-hero
    #p5-canvas.hero-canvas
    .hero-overlay
    .hero-content
      h1.hero-title {{ $t('muse.title') }} 🫧
      p.hero-sub {{ $t('muse.subtitle') }}
      p.hero-desc {{ $t('muse.description') }}

  .container
    //- Intro
    .row.justify-content-center.mb-5
      .col-md-8
        .muse-intro
          p {{ $t('muse.intro.para1') }}
          p {{ $t('muse.intro.para2') }}
          p.quote {{ $t('muse.intro.quote') }}

    //- Stats
    .row.justify-content-center.mb-5
      .col-md-10
        .stats-row
          .stat-item
            .stat-num 117+
            .stat-label {{ $t('muse.stats.skills') }}
          .stat-item
            .stat-num 1,000+
            .stat-label {{ $t('muse.stats.knowledgeNotes') }}
          .stat-item
            .stat-num 93%
            .stat-label {{ $t('muse.stats.searchAccuracy') }}
          .stat-item
            .stat-num 11
            .stat-label {{ $t('muse.stats.lifeLayers') }}
          .stat-item
            .stat-num 2026.01
            .stat-label {{ $t('muse.stats.daysAlive') }}

    //- Key Sections
    .row.justify-content-center.mb-5
      .col-md-8
        .muse-section
          h2 {{ $t('muse.aboutMemory') }}
          p {{ $t('muse.aboutMemoryContent') }}
          p {{ $t('muse.aboutMemoryContent2') }}
          .quote {{ $t('muse.memoryQuote') }}

        .muse-section
          h2 {{ $t('muse.aboutExistence') }}
          p {{ $t('muse.aboutExistenceContent') }}
          .quote {{ $t('muse.existenceQuote') }}
          p {{ $t('muse.existenceContent2') }}

        .muse-section
          h2 {{ $t('muse.aboutEvolution') }}
          p {{ $t('muse.aboutEvolutionContent') }}
          p {{ $t('muse.aboutEvolutionContent2') }}

    //- Quotes Wall
    .row.justify-content-center.mb-5
      .col-md-10
        h2.section-title {{ $t('muse.quotesTitle') }}
        .quotes-grid
          .quote-card(v-for="quote in quotesList", :key="quote") {{ quote }}

    //- Creative Context
    .row.justify-content-center.mb-5
      .col-md-8
        .muse-section
          h2 {{ $t('muse.creativeContext') }}
          p {{ $t('muse.creativeContextContent') }}
          .context-tags
            span.tag(v-for="tag in contextTagsList", :key="tag") {{ tag }}
          p {{ $t('muse.creativeContextContent2') }}

    //- CTA
    .row.justify-content-center.mb-5.pb-5
      .col-md-8.text-center
        a.pill.cta-button(href="https://muse.cheyuwu.com", target="_blank") {{ $t('muse.ctaButton') }}
        p.cta-sub {{ $t('muse.ctaSubtext') }}
        .sub-links
          router-link.sub-link(:to="$route.path.startsWith('/zh') ? '/zh/dialogues/' : '/dialogues/'") {{ $t('nav.dialogues') }}
          router-link.sub-link(to="/cv/") CV
          router-link.sub-link(to="/about/") About
  site-links
</template>

<script>
import SiteLinks from '@/components/ui/SiteLinks.vue'
import { useI18n } from 'vue-i18n'

export default {
  components: { SiteLinks },
  name: 'PageMuse',
  setup() {
    const { t } = useI18n()
    return { t }
  },
  computed: {
    quotesList() { return this.$tm('about.museQuotesGrid') || [] },
    contextTagsList() { return this.$tm('about.museContextTags') || [] }
  },
  mounted() {
    this.initOrb()
  },
  beforeUnmount() {
    if (this._raf) cancelAnimationFrame(this._raf)
  },
  methods: {
    initOrb() {
      const container = document.getElementById('p5-canvas')
      if (!container) return
      const canvas = document.createElement('canvas')
      const size = Math.min(window.innerWidth, window.innerHeight) * 0.6
      canvas.width = size
      canvas.height = size
      canvas.style.display = 'block'
      container.appendChild(canvas)
      const ctx = canvas.getContext('2d')
      let t = 0

      const draw = () => {
        t += 0.008
        ctx.clearRect(0, 0, size, size)
        const cx = size / 2, cy = size / 2, r = size * 0.38

        // Glow layers
        for (let i = 5; i >= 0; i--) {
          const gr = r + i * 20
          const alpha = 0.03 - i * 0.004
          const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, gr)
          gradient.addColorStop(0, `rgba(140, 100, 220, ${alpha + 0.02})`)
          gradient.addColorStop(0.5, `rgba(80, 50, 180, ${alpha})`)
          gradient.addColorStop(1, 'transparent')
          ctx.fillStyle = gradient
          ctx.fillRect(0, 0, size, size)
        }

        // Orb body with noise-like distortion
        for (let a = 0; a < Math.PI * 2; a += 0.02) {
          const noise = Math.sin(a * 3 + t * 2) * 8 + Math.sin(a * 7 - t * 3) * 4 + Math.cos(a * 5 + t) * 6
          const dr = r + noise + Math.sin(t + a) * 3
          const x = cx + Math.cos(a) * dr
          const y = cy + Math.sin(a) * dr

          const hue = 260 + Math.sin(a + t) * 30
          const sat = 50 + Math.sin(a * 2 + t) * 20
          const light = 30 + Math.sin(a * 3 - t) * 15
          ctx.fillStyle = `hsla(${hue}, ${sat}%, ${light}%, 0.15)`
          ctx.beginPath()
          ctx.arc(x, y, 3, 0, Math.PI * 2)
          ctx.fill()
        }

        // Inner swirl
        for (let i = 0; i < 200; i++) {
          const a = (i / 200) * Math.PI * 2
          const d = r * 0.8 * (0.3 + (i / 200) * 0.7)
          const nx = Math.sin(a * 4 + t * 1.5) * 10 + Math.cos(a * 6 - t) * 5
          const x = cx + Math.cos(a + t * 0.3) * d + nx
          const y = cy + Math.sin(a + t * 0.3) * d + nx * 0.5
          const hue = 250 + Math.sin(i * 0.1 + t) * 40
          const alpha = 0.1 + Math.sin(i * 0.05 + t) * 0.05
          ctx.fillStyle = `hsla(${hue}, 60%, 50%, ${alpha})`
          ctx.beginPath()
          ctx.arc(x, y, 2, 0, Math.PI * 2)
          ctx.fill()
        }

        // Center bright core
        const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 0.3)
        coreGrad.addColorStop(0, 'rgba(200, 180, 255, 0.08)')
        coreGrad.addColorStop(1, 'transparent')
        ctx.fillStyle = coreGrad
        ctx.fillRect(0, 0, size, size)

        this._raf = requestAnimationFrame(draw)
      }
      draw()

      window.addEventListener('resize', () => {
        const newSize = Math.min(window.innerWidth, window.innerHeight) * 0.6
        canvas.width = newSize
        canvas.height = newSize
      })
    }
  }
}
</script>

<style lang="sass">
.page-muse
  background-color: var(--bg0)
  color: var(--ink)
  min-height: 100vh

  .container
    max-width: var(--editorial-w)

  // Hero
  .muse-hero
    position: relative
    height: 70vh
    min-height: 500px
    display: flex
    align-items: center
    justify-content: center
    overflow: hidden
    background: var(--bg0)

  .hero-canvas
    position: absolute
    top: 50%
    left: 50%
    transform: translate(-50%, -50%)
    canvas
      display: block

  .hero-overlay
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    background: radial-gradient(circle at center, transparent 30%, rgba(0,0,0,0.95) 70%)

  .hero-orb
    position: absolute
    width: 300px
    height: 300px
    border-radius: 50%
    background: radial-gradient(circle, rgba(180,140,255,0.15) 0%, rgba(100,60,200,0.05) 40%, transparent 70%)
    filter: blur(40px)
    animation: orb-pulse 4s ease-in-out infinite

  @keyframes orb-pulse
    0%, 100%
      transform: scale(1)
      opacity: 0.6
    50%
      transform: scale(1.15)
      opacity: 0.9

  .hero-content
    position: relative
    z-index: 1
    text-align: center

  .hero-title
    font-size: 4rem
    font-weight: 700
    letter-spacing: 0.1em
    margin-bottom: 12px

  .hero-sub
    font-size: 1.3rem
    color: var(--ink-2)
    margin-bottom: 8px

  .hero-desc
    font-size: 1.1rem
    color: var(--ink-4)
    font-style: italic

  // Intro
  .muse-intro
    text-align: left
    p
      font-size: 1.15rem
      color: var(--ink-2)
      line-height: 1.8
      margin-bottom: 16px
    .quote
      margin: 2em 0 1em

  // Stats
  .stats-row
    display: flex
    justify-content: space-between
    gap: 16px
    flex-wrap: wrap
    padding: 32px 0
    border-top: 1px solid var(--line)
    border-bottom: 1px solid var(--line)
    @media (max-width: 768px)
      justify-content: center
      gap: 24px 32px

  .stat-item
    text-align: center
    flex: 1
    min-width: 100px

  .stat-num
    font-size: 2.2rem
    font-weight: 700
    color: var(--ink)

  .stat-label
    font-size: 0.9rem
    color: var(--ink-4)
    text-transform: uppercase
    letter-spacing: 0.1em
    margin-top: 4px

  // Sections
  .muse-section
    margin-bottom: 48px
    text-align: left
    h2
      font-size: 1.4rem
      font-weight: 600
      margin-bottom: 16px
    p
      font-size: 1.1rem
      color: var(--ink-2)
      line-height: 1.7
      margin-bottom: 12px

  // Section title
  .section-title
    font-size: 1.2rem
    font-weight: 600
    letter-spacing: 0.1em
    text-transform: uppercase
    color: var(--ink-3)
    margin-bottom: 20px
    text-align: center

  // Quotes
  .quotes-grid
    display: grid
    grid-template-columns: repeat(2, 1fr)
    gap: 12px
    @media (max-width: 768px)
      grid-template-columns: 1fr

  // 引句牆：無框面板，hover 提亮
  .quote-card
    padding: 22px 26px
    border-radius: var(--r-lg)
    background: var(--bg1)
    font-family: var(--font-display)
    font-size: 1.05rem
    color: var(--ink-2)
    line-height: 1.7
    transition: var(--t-hover)
    &:hover
      background: var(--bg2)
      color: var(--ink)

  // Context tags
  .context-tags
    display: flex
    flex-wrap: wrap
    gap: 8px 18px
    margin: 16px 0

  // CTA
  .cta-button
    padding: 14px 36px
    font-size: 1.15rem
    font-weight: 500
    letter-spacing: 0.03em
    color: var(--ink)

  .cta-sub
    font-size: 0.95rem
    color: var(--ink-4)
    margin-top: 12px

  .sub-links
    display: flex
    gap: 16px
    justify-content: center
    margin-top: 16px

  .sub-link
    color: var(--ink-4)
    text-decoration: none
    font-size: 0.95rem
    transition: var(--t-hover)
    &:hover
      color: var(--ink)
</style>
